import useCustomer from "features/Contexts/CustomerContext";
import useWorkOrder from "features/Contexts/WorkOrderContext";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import CustomerDetails from "./CustomerDetails";
import CustomerFiles from "./CustomerFile";
import CustomerWorkOrder from "./CustomerWorkOrder/CustomerWorkOrder";
// import Spinner from "components/Spinner";
import Spinner from "components/Spinner";
import CusQuotes from "./CusQuotes/CusQuotes";
import CShipping from "./Shipping/CShipping";
// The customer's shipments from Tracking Info, every courier (user,
// 2026-10-19) -- replaces the DHL-only CustomerDhlTracking tab now that the
// DHL rows live in the same table.
import UpsTracking from "pages/Sales/UpsTracking/UpsTracking";
import { canUseShipments } from "pages/Sales/UpsTracking/permission";

function CustomerDetailsLayout({}) {
  // The Tracking tab is the Shipments module for this customer (2026-10-21).
  const userInfo = useSelector((state) => state.user?.userInfo);
  const canShipments = canUseShipments(userInfo);

  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const params = useParams();
  const navigate = useNavigate();

  const { clearCustomerData, customerContextData } = useWorkOrder();
  const { advanceSearchParams, setAdvanceSearchParams } = useCustomer();

  const [searchParams, setSearchParams] = useSearchParams();

  const [allCustomersData, setAllCustomersData] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  /* ******************** Tabs ************************* */

  const [loading, setLoading] = useState(false);

  const [active, setActive] = useState(
    customerContextData ? "work Order" : "customer_details",
  );

  const checkActive = (val) => {
    if (val !== "quotes") {
      // check if param has quote-type then remove it
      let quoteType = searchParams.get("quote-type");
      if (quoteType) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("quote-type");
          return newParams;
        });
      }
    }
    setActive(val);
  };

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  const getAllCustomersData = async () => {
    try {
      let currentID = params?.selectedID;
      let page = searchParams?.get("page");
      setSelectedCustomer(null);

      if (!page) {
        const { data } = await API.get("/customers/" + currentID, {
          headers: authHeader(),
        });

        if (data?.success) {
          let result = [data.data];
          setAllCustomersData(result);
          setSelectedCustomer(data.data);
          return result;
        }
      } else {
        let query = searchParams?.get("query");
        let location = searchParams?.get("location");
        let filterBy = searchParams?.get("filterBy");

        let body = {
          page,
          filterBy,
        };

        // check for sort and order in searchParams
        let sort = searchParams.get("sort");
        let order = searchParams.get("order");

        if (sort && order) {
          body.sort = sort;
          body.order = order;
        }

        if (location && location !== "all") {
          body.location = location;
        }

        if (query) {
          body.query = query;
        }
        const { data } = await API.post("/customers", body, {
          headers: authHeader(),
        });

        setAllCustomersData(data?.result);
        let matchedOrderById = data?.result?.find(
          (item) => item?.customerId === parseInt(currentID),
        );

        if (matchedOrderById) {
          setSelectedCustomer(matchedOrderById);
          return data.result;
        }

        // The customer isn't in this page of the plain list -- which is the
        // normal case when we arrived from an advanced search, since the URL
        // still carries ?page=N from the list screen but the row was found by
        // a query that page never ran. Falling back to fetching the one
        // customer keeps the page from rendering blank.
        const { data: single } = await API.get("/customers/" + currentID, {
          headers: authHeader(),
        });

        if (single?.success) {
          setSelectedCustomer(single.data);
        }

        return data.result;
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const getCustomersByAdvanceForm = async () => {
    try {
      loadingOn();
      const { data } = await API.post(
        "/customer/advance-search",
        advanceSearchParams,
        {
          headers: authHeader(),
        },
      );
      let currentID = params?.selectedID;

      setAllCustomersData(data);
      let matchedOrderById = data?.find(
        (item) => item?.customerId === parseInt(currentID),
      );

      if (matchedOrderById) {
        setSelectedCustomer(matchedOrderById);
      }

      loadingOff();
      return data;
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const previousCustomer = () => {
    let curCustomerId = params.selectedID;

    let checkCrrIdExists = allCustomersData?.find(
      (item) => item?.customerId === parseInt(curCustomerId),
    );

    if (!checkCrrIdExists) {
      return;
    }
    let currentIndex = allCustomersData?.indexOf(selectedCustomer);
    if (currentIndex === 0) {
      return;
    }
    let newId = allCustomersData[currentIndex - 1]?.customerId;
    navigate(`/customer/details/${newId}/?${searchParams.toString()}`);
  };

  const nextCustomer = () => {
    let curCusId = params.selectedID;
    let checkCrrIdExists = allCustomersData?.find(
      (item) => item?.customerId === parseInt(curCusId),
    );
    if (!checkCrrIdExists) {
      return;
    }
    let currentIndex = allCustomersData?.indexOf(selectedCustomer);
    let totalLength = allCustomersData?.length;
    if (currentIndex === totalLength - 1) {
      return;
    }
    let newId = allCustomersData[currentIndex + 1]?.customerId;
    navigate(`/customer/details/${newId}/?${searchParams.toString()}`);
  };

  /* ******************** Tabs ************************* */
  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    let { selectedID } = params;

    if (selectedID) {
      if (advanceSearchParams) {
        getCustomersByAdvanceForm();
      } else {
        getAllCustomersData();
      }
    }
  }, [params?.selectedID, advanceSearchParams]);

  return (
    <div
      className="flex-grow-1 d-flex flex-column"
      style={{ position: "relative" }}
    >
      {loading && <Spinner />}
      {/*   Top Nav For Customer
       ********************************************* */}
      <div
        className="d-flex justify-content-end align-items-center bg-white shadow-lg ps-5 pe-4 py-2 z-3"
        style={{
          borderRadius: "10px",
          position: "fixed",
          top: 0,
          width: "calc(100vw - 200px)",
          marginLeft: 200,
          zIndex: 50,
        }}
      >
        <div className="flex-grow-1">
          <h4 className="d-flex align-items-center">
            <span className="d-inline-block me-2">
              C-{selectedCustomer?.customerId}
            </span>
            {selectedCustomer?.customerName?.toUpperCase()}
          </h4>
        </div>
        <button
          onClick={previousCustomer}
          className="btn btn-success btn-sm my-1 px-2 py-0  "
        >
          <i className="mdi mdi-arrow-left-bold fs-4 py-0 my-0"></i>
        </button>
        <button
          onClick={nextCustomer}
          className="btn btn-success mx-1 btn-sm my-1 px-2 py-0  "
        >
          <i className="mdi mdi-arrow-right-bold fs-4 py-0 my-0"></i>
        </button>
        <button
          className="btn btn-danger btn-sm"
          onClick={() => {
            clearCustomerData();
            setAdvanceSearchParams(null);
            navigate(`/customer/?${searchParams.toString()}`);
          }}
        >
          Back
        </button>
      </div>
      {/* Main Content */}
      <div className="d-flex px-2 " style={{ marginLeft: 260, marginTop: 80 }}>
        <div className=" d-flex flex-column flex-grow-1">
          <div
            className="flex-grow-1 d-flex flex-column mt-2"
            style={{ borderRadius: 10 }}
          >
            {/* Customer Details Tabs */}
            <ul className="nav nav-tabs nav-bordered mb-2  d-flex flex-grow-1">
              <li
                onClick={() => {
                  checkActive("customer_details");
                }}
                className="nav-item"
              >
                <button
                  className={
                    active === "customer_details"
                      ? `nav-link rounded-0 active bg-primary text-white`
                      : "nav-link rounded-0"
                  }
                >
                  <span className="d-none d-md-block">Customer Details</span>
                </button>
              </li>
              <li
                onClick={() => {
                  checkActive("attachments");
                }}
                className=""
              >
                <button
                  className={
                    active === "attachments"
                      ? `nav-link rounded-0 active bg-primary text-white`
                      : "nav-link rounded-0"
                  }
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Attachments</span>
                </button>
              </li>
              <li
                onClick={() => {
                  checkActive("quotes");
                }}
                className=""
              >
                <button
                  className={
                    active === "quotes"
                      ? `nav-link rounded-0 active bg-primary text-white`
                      : "nav-link rounded-0"
                  }
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Quotes</span>
                </button>
              </li>
              <li
                onClick={() => {
                  checkActive("work Order");
                }}
                className=""
              >
                <button
                  className={
                    active === "work Order"
                      ? `nav-link rounded-0 active bg-primary text-white`
                      : "nav-link rounded-0"
                  }
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Orders</span>
                </button>
              </li>
              {/* <li
                onClick={() => {
                  checkActive("shipping");
                }}
                className=""
              >
                <button
                  className={
                    active === "shipping"
                      ? `nav-link rounded-0 active bg-primary text-white`
                      : "nav-link rounded-0"
                  }
                >
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block">Shipping</span>
                </button>
              </li> */}

              {/* The Shipments module, for this customer: needs Sales ->
                  Shipments like the module itself (2026-10-21). */}
              {canShipments && (
                <li
                  onClick={() => {
                    checkActive("tracking");
                  }}
                  className=""
                >
                  <button
                    className={
                      active === "tracking"
                        ? `nav-link rounded-0 active bg-primary text-white`
                        : "nav-link rounded-0"
                    }
                  >
                    <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                    <span className="d-none d-md-block">Shipments</span>
                  </button>
                </li>
              )}

              <li className="">
                <button className={"nav-link rounded-0"}>
                  <i className="mdi mdi-settings-outline d-md-none d-block"></i>
                  <span className="d-none d-md-block"></span>
                </button>
              </li>
            </ul>
            {/* Customer Tabs Content */}
            <div className="d-flex flex-grow-1">
              {active === "customer_details" &&
                allCustomersData &&
                selectedCustomer && (
                  <CustomerDetails
                    selectedCustomer={selectedCustomer}
                    allCustomersData={allCustomersData}
                    getAllCustomersData={getAllCustomersData}
                    setSelectedCustomer={setSelectedCustomer}
                  />
                )}
              {active === "attachments" && (
                <div
                  className=" d-flex flex-grow-1 bg-white shadow-lg px-4"
                  style={{ borderRadius: 10 }}
                >
                  <CustomerFiles selectedCustomer={selectedCustomer} />
                </div>
              )}
              {active === "quotes" && selectedCustomer && (
                <div
                  className=" d-flex flex-grow-1  justify-content-start bg-white shadow-lg "
                  style={{ borderRadius: 10 }}
                >
                  <CusQuotes selectedCustomer={selectedCustomer} />
                </div>
              )}
              {active === "work Order" && selectedCustomer && (
                <div
                  className=" d-flex flex-grow-1  justify-content-start bg-white shadow-lg "
                  style={{ borderRadius: 10 }}
                >
                  <CustomerWorkOrder selectedCustomer={selectedCustomer} />
                </div>
              )}
              {active === "shipping" && selectedCustomer && (
                <div
                  className=" d-flex flex-grow-1  justify-content-start bg-white shadow-lg "
                  style={{ borderRadius: 10 }}
                >
                  <CShipping selectedCustomer={selectedCustomer} />
                </div>
              )}
              {canShipments && active === "tracking" && selectedCustomer && (
                <div
                  className=" d-flex flex-grow-1  justify-content-start bg-white shadow-lg "
                  style={{ borderRadius: 10 }}
                >
                  {/* Keyed on the customer so the arrows that switch customer
                      start the list fresh (page 1, filters cleared). */}
                  <UpsTracking
                    key={selectedCustomer.customerId}
                    customerId={selectedCustomer.customerId}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetailsLayout;
